import { useEffect, useRef, useCallback } from 'react';
import analyticsService from '../services/AnalyticsService';

/**
 * Custom hook for analytics tracking
 * Provides a clean interface for components to track events
 * without directly importing the analytics service
 */
const useAnalytics = () => {
  const gameStartTimeRef = useRef(null);
  const currentGameRef = useRef(null);

  // Get user name from localStorage (matching existing auth pattern)
  const getUserName = useCallback(() => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        return userData.name || 'unknown';
      }
    } catch (error) {
      console.warn('Analytics: Failed to get user name', error);
    }
    return 'unknown';
  }, []);

  // Track user login
  const trackLogin = useCallback(async (userName) => {
    if (!userName) {
      userName = getUserName();
    }
    return await analyticsService.trackUserLogin(userName);
  }, [getUserName]);

  // Track user logout
  const trackLogout = useCallback(async (userName, useBeacon = false) => {
    if (!userName) {
      userName = getUserName();
    }
    return await analyticsService.trackUserLogout(userName, useBeacon);
  }, [getUserName]);

  // Track game start with automatic timing
  const trackGameStart = useCallback((gameName, userName) => {
    if (!userName) {
      userName = getUserName();
    }
    
    gameStartTimeRef.current = new Date();
    currentGameRef.current = gameName;
    
    analyticsService.trackGameStart(userName, gameName);
  }, [getUserName]);

  // Track game completion with automatic duration calculation
  const trackGameComplete = useCallback((gameName, userName, metadata = {}) => {
    if (!userName) {
      userName = getUserName();
    }

    let durationSeconds = null;
    if (gameStartTimeRef.current && currentGameRef.current === gameName) {
      durationSeconds = Math.floor((new Date() - gameStartTimeRef.current) / 1000);
    }

    analyticsService.trackGameComplete(userName, gameName, durationSeconds, metadata);
    
    // Reset refs
    gameStartTimeRef.current = null;
    currentGameRef.current = null;
  }, [getUserName]);

  // Track game abandonment with automatic duration calculation
  const trackGameAbandon = useCallback((gameName, userName, metadata = {}) => {
    if (!userName) {
      userName = getUserName();
    }

    let durationSeconds = null;
    if (gameStartTimeRef.current && currentGameRef.current === gameName) {
      durationSeconds = Math.floor((new Date() - gameStartTimeRef.current) / 1000);
    }

    analyticsService.trackGameAbandon(userName, gameName, durationSeconds, metadata);
    
    // Reset refs
    gameStartTimeRef.current = null;
    currentGameRef.current = null;
  }, [getUserName]);

  // Track dashboard events
  const trackDashboardEvent = useCallback((eventType, pageOrAction = null, metadata = {}, userName) => {
    if (!userName) {
      userName = getUserName();
    }
    
    analyticsService.trackDashboardEvent(userName, eventType, pageOrAction, metadata);
  }, [getUserName]);

  // Track progress view
  const trackProgressView = useCallback((userName) => {
    trackDashboardEvent('progress_viewed', 'progress_modal', {}, userName);
  }, [trackDashboardEvent]);

  // Track game selection (before actually starting)
  const trackGameSelection = useCallback((gameName, userName) => {
    trackDashboardEvent('game_selected', gameName, { selected_game: gameName }, userName);
  }, [trackDashboardEvent]);

  // Get session info
  const getSessionInfo = useCallback(() => {
    return analyticsService.getCurrentSession();
  }, []);

  // Check if analytics is enabled
  const isEnabled = useCallback(() => {
    return analyticsService.isAnalyticsEnabled();
  }, []);

  // Manual flush for critical events
  const flush = useCallback(async () => {
    return await analyticsService.flush();
  }, []);

  // Cleanup on unmount or game change
  useEffect(() => {
    return () => {
      // If component unmounts while game is in progress, track as abandon
      if (gameStartTimeRef.current && currentGameRef.current) {
        const userName = getUserName();
        trackGameAbandon(currentGameRef.current, userName);
      }
    };
  }, [getUserName, trackGameAbandon]);

  // Return all tracking functions and utilities
  return {
    // Core tracking functions
    trackLogin,
    trackLogout,
    trackGameStart,
    trackGameComplete,
    trackGameAbandon,
    trackDashboardEvent,
    
    // Convenience functions
    trackProgressView,
    trackGameSelection,
    
    // Utilities
    getSessionInfo,
    isEnabled,
    flush,
    
    // Helper to get current user
    getUserName
  };
};

export default useAnalytics; 